import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatApiError, ROLE_LABELS } from "../lib/roles";
import { Sparkles, Mail, Lock, User, Loader2, Ticket, ArrowLeft } from "lucide-react";

export function Auth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState("");
  const [inviteRole, setInviteRole] = useState("");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reset = params.get("reset");
    if (reset) {
      api.get(`/auth/reset/${reset}`)
        .then(() => { setResetToken(reset); setMode("reset"); })
        .catch(() => setError("Il link di reset non è valido o è scaduto."));
      return;
    }
    const token = params.get("invite");
    if (token) {
      api.get(`/auth/invite/${token}`)
        .then((r) => { setInviteToken(token); setInviteRole(r.data.role); setMode("register"); })
        .catch(() => {});
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else if (mode === "register") await register(name, email, password, inviteToken);
      else if (mode === "forgot") {
        await api.post("/auth/forgot", { email });
        setNotice("Se l'email è registrata, riceverai a breve un link per reimpostare la password.");
      } else if (mode === "reset") {
        await api.post("/auth/reset", { token: resetToken, new_password: password });
        setNotice("Password aggiornata! Ora puoi accedere.");
        setPassword(""); setMode("login");
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = mode === "login" || mode === "register";
  const label = { login: "Accedi", register: "Crea account", forgot: "Invia link di reset", reset: "Imposta nuova password" }[mode];

  return (
    <div className="min-h-screen bg-grid flex items-center justify-center p-5" data-testid="auth-view">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass rounded-3xl p-8 border border-white/10">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-11 w-11 rounded-2xl grid place-items-center bg-gradient-to-br from-indigo-500 to-fuchsia-600">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-xl leading-tight">Content Studio</h1>
            <p className="text-xs text-slate-400">Il tuo centro contenuti strategico</p>
          </div>
        </div>

        {tabs && (
          <div className="flex gap-2 my-6 p-1 bg-white/5 rounded-xl">
            <button data-testid="tab-login" onClick={() => { setMode("login"); setError(""); setNotice(""); }}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>Accedi</button>
            <button data-testid="tab-register" onClick={() => { setMode("register"); setError(""); setNotice(""); }}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>Registrati</button>
          </div>
        )}

        {(mode === "forgot" || mode === "reset") && (
          <button onClick={() => { setMode("login"); setError(""); setNotice(""); }} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mt-5 mb-3">
            <ArrowLeft className="h-3.5 w-3.5" /> Torna all'accesso
          </button>
        )}

        <form onSubmit={submit} className="space-y-4">
          {mode === "forgot" && <p className="text-sm text-slate-300">Inserisci la tua email: ti invieremo un link per reimpostare la password.</p>}
          {mode === "reset" && <p className="text-sm text-slate-300">Scegli una nuova password per il tuo account.</p>}

          {inviteRole && mode === "register" && (
            <div data-testid="invite-banner" className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-sm text-emerald-100">
              <Ticket className="h-4 w-4 text-emerald-400 shrink-0" />
              Invito valido: verrai attivato subito come <strong>{ROLE_LABELS[inviteRole]}</strong>.
            </div>
          )}

          {mode === "register" && <Field icon={User} testid="auth-name" placeholder="Nome e cognome" value={name} onChange={setName} />}
          {(mode === "login" || mode === "register" || mode === "forgot") && (
            <Field icon={Mail} testid="auth-email" type="email" placeholder="La tua email" value={email} onChange={setEmail} />
          )}
          {(mode === "login" || mode === "register" || mode === "reset") && (
            <Field icon={Lock} testid="auth-password" type="password"
              placeholder={mode === "reset" ? "Nuova password (min. 6 caratteri)" : "Password (min. 6 caratteri)"} value={password} onChange={setPassword} />
          )}

          {error && <p data-testid="auth-error" className="text-sm text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}
          {notice && <p data-testid="auth-notice" className="text-sm text-emerald-300 bg-emerald-500/10 rounded-lg px-3 py-2">{notice}</p>}

          <button data-testid="auth-submit" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}{label}
          </button>
        </form>

        {mode === "login" && (
          <button data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
            className="w-full text-center text-xs text-slate-400 hover:text-white mt-4">
            Password dimenticata?
          </button>
        )}
        {mode === "register" && !inviteRole && (
          <p className="text-xs text-slate-500 mt-4 text-center">
            Dopo la registrazione il tuo accesso sarà attivato dall'amministratore con il ruolo adeguato.
          </p>
        )}
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input data-testid={testid} type={type} placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)} required
        className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3.5 py-3 text-sm focus:outline-none focus:border-indigo-400/50" />
    </div>
  );
}
